import React, { useState, useMemo } from 'react';
import {
  COMPREHENSIVE_DENTAL_INCIDENTS,
  DENTAL_RISK_DOMAINS,
  CLINICAL_SEVERITY_GUIDE,
  NON_CLINICAL_SEVERITY_GUIDE,
  getSeverityGuide,
  getLocalDateTimeString
} from '../../lib/constants';

/**
 * GuidedRiskReportWizard
 * หน้าจอแนะนำการรายงานอุบัติการณ์และความเสี่ยงแบบทีละขั้นตอน (Step-by-Step Wizard)
 * ปรับปรุง UI ให้กระชับ ตัวอักษรไม่เยอะ มีกราฟิคโมเดิร์น และแอนิเมชัน slide/fade
 * 
 * Step 1: หน้าต้อนรับ (Welcome Screen) - ตราสัญลักษณ์ 🏥🛡️🦷, หัวข้อกระชับ, ปุ่มเริ่มรายงาน
 * Step 2: หน้าเลือกรูปแบบ (Choice Screen) - การ์ดตัวเลือก 🎯 / ✍️ สวยงามพร้อมเอฟเฟกต์
 * Step 3: หน้าเลือกหัวข้อความเสี่ยง (Topic Selection Screen) - ค้นหา, เรียงงานพบบ่อยก่อน, กรอกข้อมูลเอง fallback
 * Step 4: หน้าตรวจสอบข้อมูล (Data Verification Screen) - ตรวจสอบ 4 ข้อมูลสำคัญ (ประเภทความเสี่ยง, ชื่ออุบัติการณ์, ระดับความรุนแรง, วัน เวลาที่เกิดเหตุ)
 * Step 5: หน้าข้อมูลเพิ่มเติม (Additional Information Screen) - แยกฟอร์ม 3 รูปแบบตามประเภทความเสี่ยง (คลินิก, จ่ายกลาง, ทั่วไป/สิ่งแวดล้อม)
 */
export const GuidedRiskReportWizard = ({
  initialStep = 1,
  initialIncident = null,
  initialFormData = null,
  onSelectMode = null,
  onSelectIncident = null,
  onSwitchToQuickReport = null,
  onProceedToNextStep = null,
  onBackToStep3 = null,
  onSubmit = null
}) => {
  const [step, setStep] = useState(initialStep);
  const [selectedReportMode, setSelectedReportMode] = useState(null); // 'by_topic' | 'custom' | null
  const [selectedIncident, setSelectedIncident] = useState(initialIncident);
  const [searchQuery, setSearchQuery] = useState('');
  const [direction, setDirection] = useState('forward'); // 'forward' | 'backward'

  // Submission & Reference ID State
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedRefId, setSubmittedRefId] = useState('');

  // Default Form Data State
  const defaultFormData = {
    risk_domain: 'clinical',
    category: '',
    sub_category: '',
    title: '',
    severity_level: 'C',
    incident_date: getLocalDateTimeString(new Date()),
    no_patient_involved: false,
    patient_hn: '',
    no_instrument_data: false,
    instrument_barcode: '',
    images: [],
    description: '',
    is_anonymous: false,
    reporter_name: '',
    contact_info: ''
  };

  // Form Data State for Step 4 & Step 5
  const [formData, setFormData] = useState(() => ({
    ...defaultFormData,
    risk_domain: initialIncident?.domain || initialFormData?.risk_domain || 'clinical',
    category: initialIncident?.category || initialFormData?.category || '',
    sub_category: initialIncident?.sub_category || initialFormData?.sub_category || '',
    title: initialIncident?.tag || initialFormData?.title || '',
    severity_level: initialIncident?.severity_level || initialFormData?.severity_level || 'C',
    incident_date: initialFormData?.incident_date || getLocalDateTimeString(new Date()),
    ...initialFormData
  }));

  // Handle Mode Selection in Step 2
  const handleSelectMode = (mode) => {
    setSelectedReportMode(mode);
    if (onSelectMode) {
      onSelectMode(mode);
    }
    if (mode === 'by_topic') {
      setDirection('forward');
      setStep(3);
    }
  };

  // Switch to Step 2 from Step 1
  const handleStart = () => {
    setDirection('forward');
    setStep(2);
  };

  // Reset back to Step 1 from Step 2
  const handleBackToStep1 = () => {
    setDirection('backward');
    setStep(1);
  };

  // Reset back to Step 2 from Step 3
  const handleBackToStep2 = () => {
    setDirection('backward');
    setStep(2);
  };

  // Advance to Step 4 from Step 3
  const handleProceedToStep4 = () => {
    setDirection('forward');
    setStep(4);
  };

  // Back to Step 3 from Step 4
  const handleBackToStep3 = () => {
    setDirection('backward');
    setStep(3);
    if (onBackToStep3) {
      onBackToStep3();
    }
  };

  // Proceed from Step 4 to Step 5
  const handleProceedNext = () => {
    setDirection('forward');
    setStep(5);
    if (onProceedToNextStep) {
      onProceedToNextStep(formData, selectedIncident, 5);
    }
  };

  // Back to Step 4 from Step 5
  const handleBackToStep4 = () => {
    setDirection('backward');
    setStep(4);
  };

  // Submit report from Step 5
  const handleSubmitReport = () => {
    const refId = `REF-${Date.now().toString().slice(-6)}`;
    setSubmittedRefId(refId);
    setIsSubmitted(true);
    if (onSubmit) {
      onSubmit({
        ...formData,
        reference_id: refId,
        submitted_at: new Date().toISOString()
      });
    }
  };

  // Reset wizard back to Step 1
  const handleResetWizard = () => {
    setIsSubmitted(false);
    setSubmittedRefId('');
    setSelectedReportMode(null);
    setSelectedIncident(null);
    setSearchQuery('');
    setFormData({
      ...defaultFormData,
      incident_date: getLocalDateTimeString(new Date())
    });
    setDirection('forward');
    setStep(1);
  };

  // Image upload handler
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      let previewUrl = '';
      if (typeof window !== 'undefined' && window.URL && typeof window.URL.createObjectURL === 'function') {
        try {
          previewUrl = window.URL.createObjectURL(file);
        } catch (err) {
          previewUrl = '';
        }
      }

      const reader = new FileReader();
      reader.onload = (uploadEvent) => {
        setFormData((prev) => ({
          ...prev,
          images: [
            ...(prev.images || []),
            {
              id: `${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
              name: file.name,
              size: file.size,
              dataUrl: uploadEvent.target.result,
              url: previewUrl || uploadEvent.target.result
            }
          ]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image by index
  const handleRemoveImage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, idx) => idx !== indexToRemove)
    }));
  };

  // Handle custom mode selection from dropdown bottom option in Step 3
  const handleSelectCustomFromDropdown = () => {
    setSelectedReportMode('custom');
    setSelectedIncident(null);
    if (onSelectMode) {
      onSelectMode('custom');
    }
    setFormData((prev) => ({
      ...prev,
      title: searchQuery.trim(),
      incident_date: prev.incident_date || getLocalDateTimeString(new Date())
    }));
    setDirection('forward');
    setStep(4);
  };

  // Handle Incident Selection in Step 3
  const handleSelectIncident = (incident) => {
    setSelectedIncident(incident);
    setFormData((prev) => ({
      ...prev,
      risk_domain: incident.domain || 'clinical',
      category: incident.category || '',
      sub_category: incident.sub_category || '',
      title: incident.tag || '',
      severity_level: incident.severity_level || 'C',
      incident_date: getLocalDateTimeString(new Date())
    }));
    if (onSelectIncident) {
      onSelectIncident(incident);
    }
    setDirection('forward');
    setStep(4);
  };

  // Domain change handler in Step 4
  const handleUpdateDomain = (newDomain) => {
    setFormData((prev) => {
      let newSeverity = prev.severity_level;
      if (newDomain === 'non_clinical' && /^[A-I]$/i.test(newSeverity)) {
        newSeverity = '3';
      } else if (newDomain !== 'non_clinical' && /^[1-5]$/.test(newSeverity)) {
        newSeverity = 'C';
      }
      return {
        ...prev,
        risk_domain: newDomain,
        severity_level: newSeverity
      };
    });
  };

  // Severity change handler in Step 4
  const handleUpdateSeverity = (level) => {
    setFormData((prev) => ({
      ...prev,
      severity_level: level
    }));
  };

  // Normalized search query
  const normalizedQuery = searchQuery.trim().toLowerCase();

  // Filter incidents based on searchQuery
  const filteredList = useMemo(() => {
    if (!normalizedQuery) {
      return COMPREHENSIVE_DENTAL_INCIDENTS;
    }
    return COMPREHENSIVE_DENTAL_INCIDENTS.filter((item) => {
      const matchTag = item.tag && item.tag.toLowerCase().includes(normalizedQuery);
      const matchCategory = item.category && item.category.toLowerCase().includes(normalizedQuery);
      const matchSubCategory = item.sub_category && item.sub_category.toLowerCase().includes(normalizedQuery);
      const matchDomain = item.domain && item.domain.toLowerCase().includes(normalizedQuery);
      const matchKeywords = Array.isArray(item.keywords) && item.keywords.some(
        (kw) => typeof kw === 'string' && kw.toLowerCase().includes(normalizedQuery)
      );
      return matchTag || matchCategory || matchSubCategory || matchDomain || matchKeywords;
    });
  }, [normalizedQuery]);

  // Sort logic: Items with is_frequent === true MUST appear first at the top!
  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      if (a.is_frequent && !b.is_frequent) return -1;
      if (!a.is_frequent && b.is_frequent) return 1;
      return 0;
    });
  }, [filteredList]);

  // Helper for domain badge and icon
  const getDomainMeta = (domain) => {
    switch (domain) {
      case 'clinical':
        return {
          label: 'คลินิก',
          fullLabel: 'ความเสี่ยงทางคลินิก',
          icon: '🩺',
          badgeBg: '#eff6ff',
          badgeBorder: '#bfdbfe',
          badgeColor: '#1d4ed8'
        };
      case 'cssd':
        return {
          label: 'จ่ายกลาง',
          fullLabel: 'งานจ่ายกลางและฆ่าเชื้อ',
          icon: '🧼',
          badgeBg: '#f0fdfa',
          badgeBorder: '#99f6e4',
          badgeColor: '#0f766e'
        };
      case 'non_clinical':
      default:
        return {
          label: 'ทั่วไป',
          fullLabel: 'ความเสี่ยงทั่วไป/กายภาพ',
          icon: '🏢',
          badgeBg: '#fff7ed',
          badgeBorder: '#fed7aa',
          badgeColor: '#c2410c'
        };
    }
  };

  // Helper for severity theme colors matching exact design specs
  const getSeverityTheme = (severityLevel) => {
    const code = String(severityLevel || '').trim().toUpperCase();

    // Level G, H, I (Severe Harm / Sentinel - วิกฤต) or Non-Clinical 4, 5
    if (['G', 'H', 'I', '4', '5'].includes(code)) {
      return {
        border: '#fca5a5',
        activeBorder: '#dc2626',
        bg: '#fef2f2',
        activeBg: '#fee2e2',
        text: '#991b1b',
        badgeBg: '#fee2e2',
        badgeText: '#b91c1c',
        badgeBorder: '#fecaca',
        shadowColor: '#dc2626'
      };
    }

    // Level E & F (Harm - เกิดอันตรายชั่วคราว) or Non-Clinical 3
    if (['E', 'F', '3'].includes(code)) {
      return {
        border: '#fdba74',
        activeBorder: '#ea580c',
        bg: '#fff7ed',
        activeBg: '#ffedd5',
        text: '#9a3412',
        badgeBg: '#ffedd5',
        badgeText: '#c2410c',
        badgeBorder: '#fed7aa',
        shadowColor: '#ea580c'
      };
    }

    // Level D (No Harm - Monitoring)
    if (code === 'D') {
      return {
        border: '#fde047',
        activeBorder: '#d97706',
        bg: '#fefce8',
        activeBg: '#fef9c3',
        text: '#854d0e',
        badgeBg: '#fef9c3',
        badgeText: '#a16207',
        badgeBorder: '#fef08a',
        shadowColor: '#d97706'
      };
    }

    // Level C (No Harm) or Non-Clinical 2
    if (code === 'C' || code === '2') {
      return {
        border: '#86efac',
        activeBorder: '#16a34a',
        bg: '#f0fdf4',
        activeBg: '#dcfce7',
        text: '#166534',
        badgeBg: '#dcfce7',
        badgeText: '#15803d',
        badgeBorder: '#bbf7d0',
        shadowColor: '#16a34a'
      };
    }

    // Level A & B (Near Miss) or Non-Clinical 1
    if (code === 'A' || code === 'B' || code === '1') {
      return {
        border: '#93c5fd',
        activeBorder: '#0284c7',
        bg: '#f0f9ff',
        activeBg: '#e0f2fe',
        text: '#0369a1',
        badgeBg: '#e0f2fe',
        badgeText: '#0284c7',
        badgeBorder: '#bae6fd',
        shadowColor: '#0284c7'
      };
    }

    // Default fallback
    return {
      border: '#cbd5e1',
      activeBorder: '#0284c7',
      bg: '#f8fafc',
      activeBg: '#f0f9ff',
      text: '#334155',
      badgeBg: '#e2e8f0',
      badgeText: '#475569',
      badgeBorder: '#cbd5e1',
      shadowColor: '#0284c7'
    };
  };



  // Available severity guide list for Step 4 fine-tuning
  const availableSeverityList = useMemo(() => {
    if (formData.risk_domain === 'non_clinical') {
      return NON_CLINICAL_SEVERITY_GUIDE;
    }
    return CLINICAL_SEVERITY_GUIDE;
  }, [formData.risk_domain]);

  // Active severity guideline definition for Step 4
  const currentSeverityGuide = useMemo(() => {
    return getSeverityGuide(formData.severity_level, formData.risk_domain);
  }, [formData.severity_level, formData.risk_domain]);

  const currentSeverityTheme = getSeverityTheme(formData.severity_level);
  const currentDomainMeta = getDomainMeta(formData.risk_domain);

  return (
    <div style={{ maxWidth: '880px', margin: '0 auto', padding: '10px 0' }}>
      <style>{`
        @keyframes slideFadeInRight {
          0% {
            opacity: 0;
            transform: translateX(30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideFadeInLeft {
          0% {
            opacity: 0;
            transform: translateX(-30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes floatBadge {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        @keyframes pulseGlowRing {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.85;
            transform: scale(1.05);
          }
        }
        .wizard-anim-forward {
          animation: slideFadeInRight 0.38s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .wizard-anim-backward {
          animation: slideFadeInLeft 0.38s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

      {/* ========================================================================= */}
      {/* STEP 1: หน้าต้อนรับ (Welcome Screen)                                     */}
      {/* ========================================================================= */}
      {step === 1 && (
        <div
          data-testid="wizard-step-1"
          className={direction === 'backward' ? 'wizard-anim-backward' : 'wizard-anim-forward'}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 12px 36px -8px rgba(15, 23, 42, 0.07), 0 4px 12px rgba(0, 0, 0, 0.03)',
            padding: '48px 24px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Subtle Ambient Background Gradients */}
          <div
            style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(13, 148, 136, 0.12) 0%, transparent 70%)',
              pointerEvents: 'none'
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '-40px',
              left: '-40px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(2, 132, 199, 0.12) 0%, transparent 70%)',
              pointerEvents: 'none'
            }}
          />

          {/* Graphic Badge / Emblem: Dental Shield + Tooth 🏥 🛡️ 🦷 with soft gradient glow & floating animation */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '92px',
              height: '92px',
              marginBottom: '22px',
              animation: 'floatBadge 3.2s ease-in-out infinite'
            }}
          >
            {/* Outer Glow Halo */}
            <div
              style={{
                position: 'absolute',
                inset: '-6px',
                borderRadius: '28px',
                background: 'linear-gradient(135deg, rgba(13, 148, 136, 0.4) 0%, rgba(2, 132, 199, 0.4) 100%)',
                filter: 'blur(10px)',
                zIndex: 0,
                animation: 'pulseGlowRing 3.2s ease-in-out infinite'
              }}
            />

            {/* Emblem Container */}
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                borderRadius: '26px',
                background: 'linear-gradient(135deg, #0d9488 0%, #0284c7 100%)',
                boxShadow: '0 8px 24px rgba(13, 148, 136, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                userSelect: 'none'
              }}
            >
              <span style={{ fontSize: '36px', transform: 'translate(-4px, 2px)' }}>🏥</span>
              <span style={{ fontSize: '22px', position: 'absolute', top: '10px', right: '12px' }}>🛡️</span>
              <span style={{ fontSize: '28px', position: 'absolute', bottom: '8px', right: '10px' }}>🦷</span>
            </div>
          </div>

          {/* Exact Central Headings */}
          <h1
            style={{
              fontSize: '1.95em',
              fontWeight: '900',
              color: '#0f172a',
              letterSpacing: '-0.025em',
              lineHeight: '1.25',
              margin: '0 0 10px 0'
            }}
          >
            ระบบรายงานอุบัติการณ์และความเสี่ยง
          </h1>

          <h2
            style={{
              fontSize: '1.18em',
              fontWeight: '800',
              color: '#0d9488',
              letterSpacing: '-0.01em',
              lineHeight: '1.4',
              margin: '0 0 20px 0'
            }}
          >
            ฝ่ายทันตกรรม โรงพยาบาลฝาง จังหวัดเชียงใหม่
          </h2>

          {/* Minimal 2P Safety Badge */}
          <div style={{ marginBottom: '32px' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 18px',
                borderRadius: '24px',
                backgroundColor: '#f0fdf4',
                border: '1.5px solid #86efac',
                color: '#166534',
                fontSize: '0.85em',
                fontWeight: '700',
                boxShadow: '0 2px 6px rgba(22, 101, 52, 0.05)'
              }}
            >
              <span>🛡️</span>
              <span>2P Safety Hospital Standard</span>
            </div>
          </div>

          {/* Big, Prominent Action Button & Quick Switch */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
            <button
              type="button"
              onClick={handleStart}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '16px 44px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0d9488 0%, #0284c7 100%)',
                color: '#ffffff',
                border: 'none',
                fontSize: '1.18em',
                fontWeight: '800',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(13, 148, 136, 0.35)',
                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(13, 148, 136, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(13, 148, 136, 0.35)';
              }}
            >
              <span>เริ่มรายงานความเสี่ยง</span>
              <span style={{ fontSize: '1.25em', lineHeight: '1' }}>➔</span>
            </button>

            {onSwitchToQuickReport && (
              <button
                type="button"
                onClick={onSwitchToQuickReport}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 18px',
                  borderRadius: '20px',
                  backgroundColor: '#f0f9ff',
                  border: '1.5px solid #bae6fd',
                  color: '#0284c7',
                  fontSize: '0.86em',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e0f2fe';
                  e.currentTarget.style.borderColor = '#7dd3fc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                  e.currentTarget.style.borderColor = '#bae6fd';
                }}
              >
                <span>⚡</span>
                <span>สลับไปรายงานด่วนหน้าเดียว</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* STEP 2: หน้าเลือกรูปแบบการรายงาน (Choice Selection Screen)               */}
      {/* ========================================================================= */}
      {step === 2 && (
        <div
          data-testid="wizard-step-2"
          className={direction === 'backward' ? 'wizard-anim-backward' : 'wizard-anim-forward'}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 12px 36px -8px rgba(15, 23, 42, 0.07), 0 4px 12px rgba(0, 0, 0, 0.03)',
            padding: '36px 28px',
            position: 'relative'
          }}
        >
          {/* Header Bar with Back Button and Breadcrumb Indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
              paddingBottom: '18px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '26px'
            }}
          >
            {/* Clean Back Button */}
            <button
              type="button"
              onClick={handleBackToStep1}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '12px',
                backgroundColor: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                color: '#334155',
                fontSize: '0.86em',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>← ย้อนกลับ</span>
            </button>

            {/* Breadcrumb / Step Indicator */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#f8fafc',
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                fontSize: '0.84em',
                color: '#475569',
                fontWeight: '700'
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#0284c7'
                }}
              />
              <span>ขั้นตอนที่ 1 จาก 4 • เลือกรูปแบบการรายงาน</span>
            </div>
          </div>

          {/* Concise Heading */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <h2
              style={{
                fontSize: '1.65em',
                fontWeight: '900',
                color: '#0f172a',
                letterSpacing: '-0.02em',
                margin: 0
              }}
            >
              เลือกรูปแบบการรายงาน
            </h2>
          </div>

          {/* Two Visual, Graphic Choice Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              marginBottom: '20px'
            }}
          >
            {/* ---------------- CARD 1: รายงานความเสี่ยงตามหัวข้อ ---------------- */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleSelectMode('by_topic')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectMode('by_topic');
                }
              }}
              style={{
                position: 'relative',
                borderRadius: '20px',
                padding: '24px 22px',
                border:
                  selectedReportMode === 'by_topic'
                    ? '2.5px solid #0284c7'
                    : '1.5px solid #e2e8f0',
                backgroundColor:
                  selectedReportMode === 'by_topic' ? '#f0f9ff' : '#ffffff',
                boxShadow:
                  selectedReportMode === 'by_topic'
                    ? '0 12px 28px -6px rgba(2, 132, 199, 0.28), 0 0 0 2px rgba(2, 132, 199, 0.2)'
                    : '0 4px 14px rgba(15, 23, 42, 0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                if (selectedReportMode !== 'by_topic') {
                  e.currentTarget.style.borderColor = '#93c5fd';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 22px rgba(2, 132, 199, 0.12)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedReportMode !== 'by_topic') {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(15, 23, 42, 0.04)';
                }
              }}
            >
              {/* Badge & Checkmark Pill */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '16px'
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1d4ed8',
                    fontSize: '0.78em',
                    fontWeight: '800'
                  }}
                >
                  ⭐ แนะนำ • ง่ายและเร็วที่สุด
                </span>

                {selectedReportMode === 'by_topic' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: '#0284c7',
                      color: '#ffffff',
                      fontSize: '0.76em',
                      fontWeight: '800',
                      boxShadow: '0 2px 6px rgba(2, 132, 199, 0.4)'
                    }}
                  >
                    ✓ เลือกแล้ว
                  </span>
                )}
              </div>

              {/* Graphic Icon: 🎯 in gradient circle badge */}
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  boxShadow: '0 6px 16px rgba(2, 132, 199, 0.28)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  color: '#ffffff',
                  marginBottom: '14px'
                }}
              >
                🎯
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: '1.2em',
                  fontWeight: '800',
                  color: selectedReportMode === 'by_topic' ? '#0369a1' : '#0f172a',
                  margin: '0 0 8px 0'
                }}
              >
                รายงานความเสี่ยงตามหัวข้อ
              </h3>

              {/* Short Subtitle */}
              <p
                style={{
                  fontSize: '0.88em',
                  color: '#475569',
                  lineHeight: '1.5',
                  margin: 0
                }}
              >
                เลือกจากหัวข้อที่พบบ่อย 70+ รายการ ระบบช่วยเติมข้อมูลอัตโนมัติ
              </p>
            </div>

            {/* ---------------- CARD 2: กรอกข้อมูลเอง ---------------- */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => handleSelectMode('custom')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectMode('custom');
                }
              }}
              style={{
                position: 'relative',
                borderRadius: '20px',
                padding: '24px 22px',
                border:
                  selectedReportMode === 'custom'
                    ? '2.5px solid #0d9488'
                    : '1.5px solid #e2e8f0',
                backgroundColor:
                  selectedReportMode === 'custom' ? '#f0fdfa' : '#ffffff',
                boxShadow:
                  selectedReportMode === 'custom'
                    ? '0 12px 28px -6px rgba(13, 148, 136, 0.28), 0 0 0 2px rgba(13, 148, 136, 0.2)'
                    : '0 4px 14px rgba(15, 23, 42, 0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                if (selectedReportMode !== 'custom') {
                  e.currentTarget.style.borderColor = '#99f6e4';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 22px rgba(13, 148, 136, 0.12)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedReportMode !== 'custom') {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(15, 23, 42, 0.04)';
                }
              }}
            >
              {/* Badge & Checkmark Pill */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '16px'
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: '#ccfbf1',
                    border: '1px solid #99f6e4',
                    color: '#0f766e',
                    fontSize: '0.78em',
                    fontWeight: '800'
                  }}
                >
                  พิมพ์รายละเอียดเอง
                </span>

                {selectedReportMode === 'custom' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: '#0d9488',
                      color: '#ffffff',
                      fontSize: '0.76em',
                      fontWeight: '800',
                      boxShadow: '0 2px 6px rgba(13, 148, 136, 0.4)'
                    }}
                  >
                    ✓ เลือกแล้ว
                  </span>
                )}
              </div>

              {/* Graphic Icon: ✍️ in gradient circle badge */}
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                  boxShadow: '0 6px 16px rgba(13, 148, 136, 0.28)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  color: '#ffffff',
                  marginBottom: '14px'
                }}
              >
                ✍️
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: '1.2em',
                  fontWeight: '800',
                  color: selectedReportMode === 'custom' ? '#0f766e' : '#0f172a',
                  margin: '0 0 8px 0'
                }}
              >
                กรอกข้อมูลเอง
              </h3>

              {/* Short Subtitle */}
              <p
                style={{
                  fontSize: '0.88em',
                  color: '#475569',
                  lineHeight: '1.5',
                  margin: 0
                }}
              >
                พิมพ์ระบุหัวข้อและรายละเอียดเหตุการณ์ด้วยตนเอง
              </p>
            </div>
          </div>

          {/* Brief Status Pill / Selection Confirmation */}
          {selectedReportMode && (
            <div
              data-testid="selected-mode-status"
              style={{
                marginTop: '16px',
                padding: '14px 20px',
                borderRadius: '16px',
                backgroundColor:
                  selectedReportMode === 'by_topic' ? '#f0f9ff' : '#f0fdfa',
                border:
                  selectedReportMode === 'by_topic'
                    ? '1.5px solid #bae6fd'
                    : '1.5px solid #99f6e4',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor:
                      selectedReportMode === 'by_topic' ? '#0284c7' : '#0d9488',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  ✓
                </span>
                <span
                  style={{
                    fontWeight: '800',
                    fontSize: '0.94em',
                    color:
                      selectedReportMode === 'by_topic' ? '#0369a1' : '#0f766e'
                  }}
                >
                  ✓ ท่านได้เลือก: {selectedReportMode === 'by_topic' ? 'รายงานความเสี่ยงตามหัวข้อ' : 'กรอกข้อมูลเอง'}
                </span>
              </div>

              {selectedReportMode === 'by_topic' ? (
                <button
                  type="button"
                  onClick={() => {
                    setDirection('forward');
                    setStep(3);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 14px',
                    borderRadius: '12px',
                    backgroundColor: '#0284c7',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '0.82em',
                    fontWeight: '800',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(2, 132, 199, 0.3)',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span>ไปเลือกหัวข้อต่อ ➔</span>
                </button>
              ) : (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    color: '#475569',
                    fontSize: '0.78em',
                    fontWeight: '700'
                  }}
                >
                  <span>🚀 พร้อมเข้าสู่ขั้นตอนถัดไป</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* STEP 3: หน้าเลือกหัวข้อความเสี่ยง (Topic Selection Screen)              */}
      {/* ========================================================================= */}
      {step === 3 && (
        <div
          data-testid="wizard-step-3"
          className={direction === 'backward' ? 'wizard-anim-backward' : 'wizard-anim-forward'}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 12px 36px -8px rgba(15, 23, 42, 0.07), 0 4px 12px rgba(0, 0, 0, 0.03)',
            padding: '36px 28px',
            position: 'relative'
          }}
        >
          {/* Header Bar with Back Button and Breadcrumb Indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
              paddingBottom: '18px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '24px'
            }}
          >
            {/* Clean Back Button */}
            <button
              type="button"
              onClick={handleBackToStep2}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '12px',
                backgroundColor: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                color: '#334155',
                fontSize: '0.86em',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>← ย้อนกลับ</span>
            </button>

            {/* Breadcrumb / Step Indicator */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#f8fafc',
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                fontSize: '0.84em',
                color: '#475569',
                fontWeight: '700'
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#0284c7'
                }}
              />
              <span>ขั้นตอนที่ 2 จาก 4 • เลือกหัวข้ออุบัติการณ์</span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div style={{ textAlign: 'center', marginBottom: '22px' }}>
            <h2
              style={{
                fontSize: '1.65em',
                fontWeight: '900',
                color: '#0f172a',
                letterSpacing: '-0.02em',
                margin: '0 0 6px 0'
              }}
            >
              เลือกหัวข้ออุบัติการณ์ความเสี่ยง
            </h2>
            <p
              style={{
                fontSize: '0.92em',
                color: '#64748b',
                margin: 0,
                lineHeight: '1.5'
              }}
            >
              แตะเลือกเหตุการณ์ที่เกิดขึ้นเพื่อเติมข้อมูลและประเมินระดับความรุนแรงให้อัตโนมัติ
            </p>
          </div>

          {/* Search Box & Dropdown Autocomplete */}
          <div style={{ position: 'relative', marginBottom: '24px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f8fafc',
                border: '1.5px solid #cbd5e1',
                borderRadius: '16px',
                padding: '4px 14px',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
                transition: 'all 0.15s ease'
              }}
            >
              <span style={{ fontSize: '18px', marginRight: '10px', color: '#64748b' }}>🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ค้นหาหัวข้ออุบัติการณ์... เช่น เข็ม, ยาชา, หัก, เปียก, ไฟดับ, ตกค้าง, กลืน"
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  padding: '12px 0',
                  fontSize: '0.95em',
                  color: '#0f172a',
                  outline: 'none',
                  fontWeight: '600'
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="ล้างคำค้นหา"
                  style={{
                    background: '#e2e8f0',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#475569',
                    marginRight: '8px'
                  }}
                  title="ล้างคำค้นหา"
                >
                  ✕
                </button>
              )}
              {searchQuery.trim().length > 0 && (
                <div
                  style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    backgroundColor: '#e2e8f0',
                    color: '#334155',
                    fontSize: '0.78em',
                    fontWeight: '700',
                    whiteSpace: 'nowrap'
                  }}
                >
                  พบ {sortedList.length} รายการ
                </div>
              )}
            </div>

            {/* Subtle Hint below search box when query is empty */}
            {!searchQuery.trim() && (
              <div
                style={{
                  marginTop: '14px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: '0.88em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>💡</span>
                <span>พิมพ์คำค้นหาเพื่อเลือกหัวข้ออุบัติการณ์ (เช่น เข็ม, ยาชา, หัก, เปียก, สปอร์)</span>
              </div>
            )}

            {/* Floating/Card Dropdown List when searchQuery.trim().length > 0 */}
            {searchQuery.trim().length > 0 && (
              <div
                data-testid="search-results-dropdown"
                style={{
                  position: 'relative',
                  marginTop: '8px',
                  borderRadius: '16px',
                  border: '1.5px solid #cbd5e1',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                  maxHeight: '380px',
                  overflowY: 'auto',
                  backgroundColor: '#ffffff'
                }}
              >
                {/* Matching Items or 0 Match Note */}
                {sortedList.length === 0 ? (
                  <div
                    style={{
                      padding: '24px 20px',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '0.92em',
                      fontWeight: '600'
                    }}
                  >
                    🔎 ไม่พบหัวข้อที่ตรงกับ "{searchQuery}"
                  </div>
                ) : (
                  <div>
                    {sortedList.map((item) => {
                      const domainMeta = getDomainMeta(item.domain);
                      const theme = getSeverityTheme(item.severity_level);
                      const icon = item.is_frequent ? '🔥' : domainMeta.icon;

                      return (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          data-testid={`incident-dropdown-item-${item.id}`}
                          onClick={() => handleSelectIncident(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleSelectIncident(item);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            padding: '12px 18px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: '18px', flexShrink: 0 }}>{icon}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span
                                style={{
                                  fontSize: '0.92em',
                                  fontWeight: '700',
                                  color: '#0f172a',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {item.tag}
                              </span>
                              {(item.sub_category || item.category) && (
                                <span
                                  style={{
                                    fontSize: '0.76em',
                                    color: '#64748b'
                                  }}
                                >
                                  {item.category}{item.sub_category ? ` • ${item.sub_category}` : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          <span
                            data-testid={`dropdown-severity-${item.id}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              fontSize: '0.74em',
                              fontWeight: '800',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: theme.badgeBg,
                              color: theme.badgeText,
                              border: `1px solid ${theme.badgeBorder}`,
                              lineHeight: 1.3,
                              flexShrink: 0
                            }}
                          >
                            Level {item.severity_level}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bottom-most Always-Present Option */}
                <div
                  role="button"
                  tabIndex={0}
                  data-testid="dropdown-custom-mode-option"
                  onClick={handleSelectCustomFromDropdown}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectCustomFromDropdown();
                    }
                  }}
                  style={{
                    borderTop: '1.5px solid #99f6e4',
                    background: '#f0fdfa',
                    color: '#0f766e',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '0.88em',
                    fontWeight: '700',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ccfbf1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0fdfa';
                  }}
                >
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>✍️</span>
                  <span style={{ flex: 1 }}>
                    ไม่พบเหตุการณ์ที่ตรงกับกรณีของท่าน กดเลือกตัวเลือกนี้เพื่อกรอกข้อมูลเอง
                  </span>
                  <span style={{ fontSize: '14px', color: '#0d9488' }}>➔</span>
                </div>
              </div>
            )}
          </div>

          {/* Selected Incident Status Pill (Concise single-line confirmation) */}
          {selectedIncident && (
            <div
              data-testid="selected-incident-status"
              style={{
                marginTop: '18px',
                padding: '12px 18px',
                borderRadius: '16px',
                backgroundColor: getSeverityTheme(selectedIncident.severity_level).bg,
                border: `1.5px solid ${getSeverityTheme(selectedIncident.severity_level).border}`,
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '220px' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: getSeverityTheme(selectedIncident.severity_level).activeBorder,
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}
                >
                  ✓
                </span>
                <span
                  style={{
                    fontWeight: '800',
                    fontSize: '0.9em',
                    color: getSeverityTheme(selectedIncident.severity_level).text
                  }}
                >
                  ✓ เลือก: {selectedIncident.tag} (Level {selectedIncident.severity_level})
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    border: `1px solid ${getSeverityTheme(selectedIncident.severity_level).badgeBorder}`,
                    color: getSeverityTheme(selectedIncident.severity_level).badgeText,
                    fontSize: '0.78em',
                    fontWeight: '700'
                  }}
                >
                  <span>🚀 พร้อมสำหรับเข้าสู่ขั้นตอนถัดไป</span>
                </div>

                <button
                  type="button"
                  data-testid="btn-proceed-to-step4"
                  onClick={handleProceedToStep4}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 18px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #0d9488 0%, #0284c7 100%)',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '0.86em',
                    fontWeight: '800',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(13, 148, 136, 0.3)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(13, 148, 136, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 148, 136, 0.3)';
                  }}
                >
                  <span>ไปตรวจสอบข้อมูล</span>
                  <span style={{ fontSize: '1.1em', lineHeight: 1 }}>➔</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* STEP 4: หน้าตรวจสอบข้อมูล (Data Verification Screen)                      */}
      {/* ========================================================================= */}
      {step === 4 && (
        <div
          data-testid="wizard-step-4"
          className={direction === 'backward' ? 'wizard-anim-backward' : 'wizard-anim-forward'}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 12px 36px -8px rgba(15, 23, 42, 0.07), 0 4px 12px rgba(0, 0, 0, 0.03)',
            padding: '36px 28px',
            position: 'relative'
          }}
        >
          {/* Header Bar with Back Button and Breadcrumb Indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
              paddingBottom: '18px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '24px'
            }}
          >
            {/* Clean Back Button */}
            <button
              type="button"
              data-testid="header-back-to-step3"
              onClick={handleBackToStep3}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '12px',
                backgroundColor: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                color: '#334155',
                fontSize: '0.86em',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>← ย้อนกลับ</span>
            </button>

            {/* Breadcrumb / Step Indicator */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#f8fafc',
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                fontSize: '0.84em',
                color: '#475569',
                fontWeight: '700'
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#0284c7'
                }}
              />
              <span>ขั้นตอนที่ 3 จาก 4 • ตรวจสอบข้อมูลเบื้องต้น</span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div style={{ textAlign: 'center', marginBottom: '26px' }}>
            <h2
              style={{
                fontSize: '1.65em',
                fontWeight: '900',
                color: '#0f172a',
                letterSpacing: '-0.02em',
                margin: '0 0 6px 0'
              }}
            >
              ตรวจสอบข้อมูลอุบัติการณ์
            </h2>
            <p
              style={{
                fontSize: '0.92em',
                color: '#64748b',
                margin: 0,
                lineHeight: '1.5'
              }}
            >
              ระบบได้กรอกและเลือกข้อมูลให้อัตโนมัติตามหัวข้อที่ท่านเลือก กรุณาตรวจสอบก่อนไปขั้นตอนถัดไป
            </p>
          </div>

          {/* 1. ประเภทความเสี่ยง (Risk Domain) */}
          <div
            data-testid="section-risk-domain"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '20px 22px',
              marginBottom: '20px'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px',
                marginBottom: '14px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>🛡️</span>
                <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  1. ประเภทความเสี่ยง (Risk Domain)
                </h3>
              </div>

              {/* Domain Badge with Icon */}
              <span
                data-testid="badge-risk-domain"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 14px',
                  borderRadius: '20px',
                  backgroundColor: currentDomainMeta.badgeBg,
                  border: `1.5px solid ${currentDomainMeta.badgeBorder}`,
                  color: currentDomainMeta.badgeColor,
                  fontSize: '0.85em',
                  fontWeight: '800'
                }}
              >
                <span>{currentDomainMeta.icon}</span>
                <span>{currentDomainMeta.fullLabel}</span>
              </span>
            </div>

            {/* Category & Subcategory Details */}
            <div
              data-testid="category-subcategory-details"
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                padding: '12px 16px',
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px',
                fontSize: '0.9em',
                color: '#334155'
              }}
            >
              <span style={{ fontWeight: '800', color: '#0f172a' }}>หมวดหมู่:</span>
              <span style={{ fontWeight: '700', color: '#0284c7' }}>
                {formData.category || 'หมวดหมู่ทั่วไป'}
              </span>
              {formData.sub_category && (
                <>
                  <span style={{ color: '#94a3b8' }}>•</span>
                  <span style={{ color: '#475569', fontWeight: '600' }}>{formData.sub_category}</span>
                </>
              )}
            </div>

            {/* Quick selector to adjust domain if needed */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700' }}>
                ปรับเปลี่ยนประเภท:
              </span>
              {DENTAL_RISK_DOMAINS.map((dm) => {
                const isSelected = formData.risk_domain === dm.id;
                return (
                  <button
                    key={dm.id}
                    type="button"
                    data-testid={`btn-domain-${dm.id}`}
                    onClick={() => handleUpdateDomain(dm.id)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: '14px',
                      border: isSelected ? `2px solid ${dm.color}` : '1px solid #cbd5e1',
                      backgroundColor: isSelected ? '#ffffff' : '#f1f5f9',
                      color: isSelected ? dm.color : '#64748b',
                      fontSize: '0.8em',
                      fontWeight: '800',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <span>{dm.icon}</span> {dm.label.split(' ')[0]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2. ชื่ออุบัติการณ์ (Incident Title) */}
          <div
            data-testid="section-incident-title"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '20px 22px',
              marginBottom: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>📝</span>
              <div>
                <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  2. ชื่ออุบัติการณ์ (Incident Title)
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.8em', color: '#64748b' }}>
                  ระบบกรอกให้อัตโนมัติ สามารถปรับแต่งหรือระบุรายละเอียดเฉพาะเจาะจงเพิ่มเติมได้
                </p>
              </div>
            </div>

            <input
              type="text"
              data-testid="input-incident-title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="เช่น ตะไบหักในคลองรากฟัน ซี่ 16"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px 16px',
                borderRadius: '14px',
                border: '1.5px solid #cbd5e1',
                backgroundColor: '#ffffff',
                fontSize: '0.96em',
                fontWeight: '700',
                color: '#0f172a',
                outline: 'none',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
                transition: 'border-color 0.15s ease'
              }}
            />
            <div style={{ marginTop: '8px', fontSize: '0.78em', color: '#64748b' }}>
              💡 ตัวอย่าง: เพิ่มเติมตำแหน่งซี่ฟัน เครื่องมือ หรือบริเวณที่เกิดเหตุ เช่น "ตะไบหักในคลองรากฟัน ซี่ 16"
            </div>
          </div>

          {/* 3. ระดับความรุนแรง (Severity Level) */}
          <div
            data-testid="section-severity-level"
            style={{
              backgroundColor: currentSeverityTheme.bg,
              borderRadius: '18px',
              border: `1.5px solid ${currentSeverityTheme.border}`,
              padding: '20px 22px',
              marginBottom: '20px'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '10px',
                marginBottom: '14px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>⚡</span>
                <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: currentSeverityTheme.text, margin: 0 }}>
                  3. ระดับความรุนแรง (Severity Level)
                </h3>
              </div>

              {/* Severity Badge */}
              <span
                data-testid="badge-severity-level"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 14px',
                  borderRadius: '20px',
                  backgroundColor: currentSeverityTheme.badgeBg,
                  border: `1.5px solid ${currentSeverityTheme.badgeBorder}`,
                  color: currentSeverityTheme.badgeText,
                  fontSize: '0.9em',
                  fontWeight: '900'
                }}
              >
                Level {formData.severity_level}
              </span>
            </div>

            {/* Severity definition card */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '14px',
                border: `1px solid ${currentSeverityTheme.border}`,
                padding: '14px 18px',
                marginBottom: '16px'
              }}
            >
              <div style={{ fontWeight: '800', fontSize: '0.96em', color: currentSeverityTheme.text, marginBottom: '6px' }}>
                {currentSeverityGuide?.name || `ระดับความรุนแรง Level ${formData.severity_level}`}
              </div>
              {currentSeverityGuide?.description && (
                <div style={{ fontSize: '0.86em', color: '#475569', lineHeight: '1.5', marginBottom: '10px' }}>
                  {currentSeverityGuide.description}
                </div>
              )}

              {/* Action timeline & RCA indicator badges */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap',
                  paddingTop: '8px',
                  borderTop: '1px dashed #e2e8f0'
                }}
              >
                {currentSeverityGuide?.action_timeline && (
                  <span
                    data-testid="badge-action-timeline"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '0.78em',
                      fontWeight: '700',
                      color: '#1e293b',
                      backgroundColor: '#f1f5f9',
                      padding: '3px 10px',
                      borderRadius: '10px'
                    }}
                  >
                    ⏱️ {currentSeverityGuide.action_timeline}
                  </span>
                )}
                <span
                  data-testid="badge-rca-status"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.78em',
                    fontWeight: '800',
                    padding: '3px 10px',
                    borderRadius: '10px',
                    backgroundColor: currentSeverityGuide?.rca_required ? '#fee2e2' : '#dcfce7',
                    color: currentSeverityGuide?.rca_required ? '#b91c1c' : '#15803d',
                    border: `1px solid ${currentSeverityGuide?.rca_required ? '#fecaca' : '#bbf7d0'}`
                  }}
                >
                  {currentSeverityGuide?.rca_required ? '⚠️ ต้องทำ RCA' : '🛡️ ไม่ต้องทำ RCA'}
                </span>
              </div>
            </div>

            {/* Quick selector to fine-tune level */}
            <div>
              <div style={{ fontSize: '0.78em', color: '#64748b', fontWeight: '700', marginBottom: '8px' }}>
                ปรับเปลี่ยนระดับความรุนแรง:
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {availableSeverityList.map((item) => {
                  const isSelected = String(formData.severity_level).toUpperCase() === String(item.code).toUpperCase();
                  const itemTheme = getSeverityTheme(item.code);
                  return (
                    <button
                      key={item.code}
                      type="button"
                      data-testid={`btn-select-severity-${item.code}`}
                      onClick={() => handleUpdateSeverity(item.code)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '12px',
                        border: isSelected ? `2.5px solid ${itemTheme.activeBorder}` : `1px solid ${itemTheme.border}`,
                        backgroundColor: isSelected ? itemTheme.activeBg : '#ffffff',
                        color: itemTheme.text,
                        fontSize: '0.82em',
                        fontWeight: '800',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {isSelected && '✓ '}Level {item.code}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 4. วัน เวลาที่เกิดเหตุ (Incident Date & Time) */}
          <div
            data-testid="section-incident-datetime"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '20px 22px',
              marginBottom: '26px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>🕒</span>
              <div>
                <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  4. วัน เวลาที่เกิดเหตุ (Incident Date & Time)
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.8em', color: '#64748b' }}>
                  ค่าเริ่มต้นคือเวลาปัจจุบัน สามารถกดเปลี่ยนเวลาได้หากเกิดเหตุในเวลาอื่น
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="datetime-local"
                data-testid="input-incident-date"
                value={formData.incident_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, incident_date: e.target.value }))}
                style={{
                  padding: '12px 16px',
                  borderRadius: '14px',
                  border: '1.5px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  fontSize: '0.96em',
                  fontWeight: '700',
                  color: '#0f172a',
                  outline: 'none',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
                  flex: 1,
                  minWidth: '220px'
                }}
              />

              <button
                type="button"
                data-testid="btn-stamp-now"
                onClick={() => setFormData((prev) => ({ ...prev, incident_date: getLocalDateTimeString(new Date()) }))}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '12px 18px',
                  borderRadius: '14px',
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #0284c7',
                  color: '#0284c7',
                  fontSize: '0.88em',
                  fontWeight: '800',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.08)',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f9ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <span>🕒</span>
                <span>ลงเวลาปัจจุบัน</span>
              </button>
            </div>
          </div>

          {/* Bottom Navigation Actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '14px',
              paddingTop: '20px',
              borderTop: '1px solid #f1f5f9'
            }}
          >
            <button
              type="button"
              data-testid="btn-back-to-step3"
              onClick={handleBackToStep3}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 22px',
                borderRadius: '14px',
                backgroundColor: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                color: '#334155',
                fontSize: '0.92em',
                fontWeight: '800',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>← ย้อนกลับไปเลือกหัวข้อ</span>
            </button>

            <button
              type="button"
              data-testid="btn-proceed-to-next-step"
              onClick={handleProceedNext}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 32px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0d9488 0%, #0284c7 100%)',
                color: '#ffffff',
                border: 'none',
                fontSize: '1.05em',
                fontWeight: '800',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(13, 148, 136, 0.35)',
                transition: 'all 0.18s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(13, 148, 136, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(13, 148, 136, 0.35)';
              }}
            >
              <span>ไปขั้นตอนถัดไป</span>
              <span style={{ fontSize: '1.2em', lineHeight: 1 }}>➔</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SUCCESS SCREEN: บันทึกรายงานความเสี่ยงเรียบร้อยแล้ว                      */}
      {/* ========================================================================= */}
      {isSubmitted && (
        <div
          data-testid="wizard-success-screen"
          className="wizard-anim-forward"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            border: '1.5px solid #bbf7d0',
            boxShadow: '0 12px 36px -8px rgba(16, 185, 129, 0.12), 0 4px 12px rgba(0, 0, 0, 0.03)',
            padding: '44px 32px',
            textAlign: 'center',
            position: 'relative'
          }}
        >
          {/* Success Checkmark Badge */}
          <div
            style={{
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              backgroundColor: '#dcfce7',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '38px',
              fontWeight: '900',
              margin: '0 auto 18px auto',
              boxShadow: '0 4px 16px rgba(22, 163, 74, 0.2)'
            }}
          >
            ✓
          </div>

          <h2
            style={{
              fontSize: '1.65em',
              fontWeight: '900',
              color: '#0f172a',
              letterSpacing: '-0.02em',
              margin: '0 0 8px 0'
            }}
          >
            บันทึกรายงานความเสี่ยงเรียบร้อยแล้ว
          </h2>
          <p
            style={{
              fontSize: '0.92em',
              color: '#64748b',
              margin: '0 0 20px 0'
            }}
          >
            ข้อมูลถูกส่งไปยังระบบบริหารความเสี่ยงทันตกรรมและงานจ่ายกลางเรียบร้อยแล้ว
          </p>

          {/* Reference ID badge */}
          <div
            data-testid="badge-reference-id"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 22px',
              borderRadius: '20px',
              backgroundColor: '#f1f5f9',
              border: '1.5px solid #cbd5e1',
              fontSize: '0.95em',
              fontWeight: '800',
              color: '#1e293b',
              marginBottom: '26px'
            }}
          >
            <span>รหัสอ้างอิง:</span>
            <span style={{ color: '#0284c7', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
              {submittedRefId}
            </span>
          </div>

          {/* Incident Summary Details */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '22px 26px',
              textAlign: 'left',
              maxWidth: '560px',
              margin: '0 auto 28px auto'
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '10px', fontSize: '0.88em' }}>
              <span style={{ color: '#64748b', fontWeight: '700' }}>หัวข้ออุบัติการณ์:</span>
              <span style={{ color: '#0f172a', fontWeight: '800' }}>{formData.title || '-'}</span>

              <span style={{ color: '#64748b', fontWeight: '700' }}>ประเภท:</span>
              <span style={{ color: currentDomainMeta.badgeColor, fontWeight: '800' }}>
                {currentDomainMeta.icon} {currentDomainMeta.fullLabel}
              </span>

              <span style={{ color: '#64748b', fontWeight: '700' }}>ระดับความรุนแรง:</span>
              <span style={{ color: currentSeverityTheme.badgeText, fontWeight: '800' }}>
                Level {formData.severity_level}
              </span>

              <span style={{ color: '#64748b', fontWeight: '700' }}>วัน เวลาที่เกิดเหตุ:</span>
              <span style={{ color: '#334155', fontWeight: '700' }}>{formData.incident_date}</span>

              {formData.risk_domain === 'clinical' && (
                <>
                  <span style={{ color: '#64748b', fontWeight: '700' }}>ข้อมูลผู้ป่วย:</span>
                  <span style={{ color: '#334155', fontWeight: '700' }}>
                    {formData.no_patient_involved
                      ? 'ไม่มีผู้ป่วยเกี่ยวข้อง'
                      : (formData.patient_hn ? `HN: ${formData.patient_hn}` : 'ไม่ได้ระบุ HN')}
                  </span>
                </>
              )}

              {formData.risk_domain === 'cssd' && (
                <>
                  <span style={{ color: '#64748b', fontWeight: '700' }}>ข้อมูลเครื่องมือ:</span>
                  <span style={{ color: '#334155', fontWeight: '700' }}>
                    {formData.no_instrument_data
                      ? 'ไม่มีข้อมูลของเครื่องมือ'
                      : (formData.instrument_barcode ? `บาร์โค้ด: ${formData.instrument_barcode}` : 'ไม่ได้ระบุบาร์โค้ด')}
                  </span>
                </>
              )}

              <span style={{ color: '#64748b', fontWeight: '700' }}>ผู้รายงาน:</span>
              <span style={{ color: '#334155', fontWeight: '700' }}>
                {formData.is_anonymous ? 'นิรนาม (ไม่เปิดเผยตัวตน)' : (formData.reporter_name || 'ไม่ได้ระบุชื่อ')}
              </span>

              {formData.images && formData.images.length > 0 && (
                <>
                  <span style={{ color: '#64748b', fontWeight: '700' }}>รูปภาพแนบ:</span>
                  <span style={{ color: '#0284c7', fontWeight: '800' }}>
                    {formData.images.length} ภาพ
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Reset / Report New Incident Button */}
          <button
            type="button"
            data-testid="btn-reset-wizard"
            onClick={handleResetWizard}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 28px',
              borderRadius: '14px',
              backgroundColor: '#ffffff',
              border: '1.5px solid #0284c7',
              color: '#0284c7',
              fontSize: '0.95em',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(2, 132, 199, 0.1)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f9ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            <span>🔄 เริ่มรายงานอุบัติการณ์ใหม่</span>
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* STEP 5: หน้าข้อมูลเพิ่มเติม (Additional Information Screen)                */}
      {/* ========================================================================= */}
      {!isSubmitted && step === 5 && (
        <div
          data-testid="wizard-step-5"
          className={direction === 'backward' ? 'wizard-anim-backward' : 'wizard-anim-forward'}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 12px 36px -8px rgba(15, 23, 42, 0.07), 0 4px 12px rgba(0, 0, 0, 0.03)',
            padding: '36px 28px',
            position: 'relative'
          }}
        >
          {/* Top Header Bar with Back Button and Breadcrumb Indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
              paddingBottom: '18px',
              borderBottom: '1px solid #f1f5f9',
              marginBottom: '24px'
            }}
          >
            <button
              type="button"
              data-testid="header-back-to-step4"
              onClick={handleBackToStep4}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '12px',
                backgroundColor: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                color: '#334155',
                fontSize: '0.86em',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>← ย้อนกลับไปตรวจสอบข้อมูล</span>
            </button>

            {/* Breadcrumb / Step Indicator */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#f8fafc',
                padding: '6px 14px',
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                fontSize: '0.84em',
                color: '#475569',
                fontWeight: '700'
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#0d9488'
                }}
              />
              <span>ขั้นตอนที่ 4 จาก 4 • ข้อมูลเพิ่มเติมตามประเภทความเสี่ยง</span>
            </div>
          </div>

          {/* Title & Dynamic Subtitle */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h2
              style={{
                fontSize: '1.65em',
                fontWeight: '900',
                color: '#0f172a',
                letterSpacing: '-0.02em',
                margin: '0 0 6px 0'
              }}
            >
              ระบุข้อมูลเพิ่มเติม
            </h2>
            <p
              style={{
                fontSize: '0.92em',
                color: '#64748b',
                margin: 0,
                lineHeight: 1.5
              }}
            >
              {formData.risk_domain === 'clinical' &&
                'ระบุข้อมูลผู้ป่วยและแนบภาพถ่ายประกอบ (ถ้ามี) เพื่อการดูแลรักษาและติดตามเคสอย่างแม่นยำ'}
              {formData.risk_domain === 'cssd' &&
                'ระบุข้อมูลบาร์โค้ดเครื่องมือและแนบภาพถ่ายประกอบ (ถ้ามี) เพื่อการสอบย้อนกลับและตรวจสอบรอบนึ่ง'}
              {formData.risk_domain === 'non_clinical' &&
                'แนบภาพถ่ายประกอบ (ถ้ามี) และระบุข้อมูลสิ่งแวดล้อมเพื่อการปรับปรุงแก้ไขอย่างรวดเร็ว'}
            </p>
          </div>

          {/* Compact Incident Summary Pill at Top */}
          <div
            data-testid="compact-incident-summary"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '16px',
              border: '1.5px solid #e2e8f0',
              padding: '12px 18px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.1em' }}>📋</span>
              <span style={{ fontSize: '0.9em', fontWeight: '800', color: '#0f172a' }}>
                หัวข้อ: {formData.title || '(ยังไม่ระบุหัวข้อ)'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.78em',
                  fontWeight: '700',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  backgroundColor: currentDomainMeta.badgeBg,
                  border: `1px solid ${currentDomainMeta.badgeBorder}`,
                  color: currentDomainMeta.badgeColor
                }}
              >
                <span>{currentDomainMeta.icon}</span>
                <span>{currentDomainMeta.label}</span>
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontSize: '0.78em',
                  fontWeight: '800',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  backgroundColor: currentSeverityTheme.badgeBg,
                  border: `1px solid ${currentSeverityTheme.badgeBorder}`,
                  color: currentSeverityTheme.badgeText
                }}
              >
                Level {formData.severity_level}
              </span>
            </div>
          </div>

          {/* ================= DYNAMIC DOMAIN FORMATS ================= */}

          {/* FORMAT 1: ความเสี่ยงทางคลินิก (Clinical) */}
          {formData.risk_domain === 'clinical' && (
            <div
              data-testid="section-clinical-additional"
              style={{
                backgroundColor: '#f8fafc',
                borderRadius: '18px',
                border: '1.5px solid #e2e8f0',
                padding: '20px 22px',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span style={{ fontSize: '20px' }}>🩺</span>
                <div>
                  <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                    ข้อมูลผู้ป่วยที่เกี่ยวข้อง (Patient Information)
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.8em', color: '#64748b' }}>
                    ระบุเลขประจำตัวผู้ป่วย หรือติ๊กเลือกหากเป็นเหตุการณ์ที่ไม่เกี่ยวข้องกับผู้ป่วยรายบุคคล
                  </p>
                </div>
              </div>

              {/* Checkbox: ไม่มีผู้ป่วยเกี่ยวข้อง */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '12px 14px',
                  backgroundColor: formData.no_patient_involved ? '#eff6ff' : '#ffffff',
                  border: `1.5px solid ${formData.no_patient_involved ? '#3b82f6' : '#cbd5e1'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  marginBottom: '16px',
                  transition: 'all 0.15s ease'
                }}
              >
                <input
                  type="checkbox"
                  id="checkbox-no-patient"
                  data-testid="checkbox-no-patient"
                  checked={formData.no_patient_involved}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      no_patient_involved: e.target.checked,
                      patient_hn: e.target.checked ? '' : prev.patient_hn
                    }))
                  }
                  style={{ marginTop: '3px', cursor: 'pointer', width: '17px', height: '17px' }}
                />
                <div>
                  <span style={{ fontSize: '0.9em', fontWeight: '700', color: '#1e293b' }}>
                    ไม่มีผู้ป่วยเกี่ยวข้องกับเหตุการณ์นี้
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78em', color: '#64748b', marginTop: '2px' }}>
                    (เช่น ตรวจพบก่อนเริ่มการรักษา หรือไม่เกี่ยวกับผู้ป่วยรายบุคคล)
                  </span>
                </div>
              </label>

              {/* Patient HN Field */}
              <div>
                <label
                  htmlFor="input-patient-hn"
                  style={{
                    display: 'block',
                    fontSize: '0.85em',
                    fontWeight: '700',
                    color: formData.no_patient_involved ? '#94a3b8' : '#334155',
                    marginBottom: '6px'
                  }}
                >
                  เลขประจำตัวผู้ป่วย (HN)
                </label>
                <input
                  id="input-patient-hn"
                  type="text"
                  data-testid="input-patient-hn"
                  disabled={formData.no_patient_involved}
                  value={formData.patient_hn}
                  onChange={(e) => setFormData((prev) => ({ ...prev, patient_hn: e.target.value }))}
                  placeholder={
                    formData.no_patient_involved
                      ? '(ไม่มีผู้ป่วยเกี่ยวข้องกับเหตุการณ์นี้)'
                      : 'เช่น 123456 หรือ 67/01234'
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '14px',
                    border: `1.5px solid ${formData.no_patient_involved ? '#e2e8f0' : '#cbd5e1'}`,
                    backgroundColor: formData.no_patient_involved ? '#f1f5f9' : '#ffffff',
                    color: formData.no_patient_involved ? '#94a3b8' : '#0f172a',
                    fontSize: '0.95em',
                    fontWeight: '700',
                    outline: 'none',
                    cursor: formData.no_patient_involved ? 'not-allowed' : 'text',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          )}

          {/* FORMAT 2: งานประเภทจ่ายกลางและฆ่าเชื้อ (CSSD) */}
          {formData.risk_domain === 'cssd' && (
            <div
              data-testid="section-cssd-additional"
              style={{
                backgroundColor: '#f8fafc',
                borderRadius: '18px',
                border: '1.5px solid #e2e8f0',
                padding: '20px 22px',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span style={{ fontSize: '20px' }}>🧼</span>
                <div>
                  <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                    ข้อมูลเครื่องมือและรอบการฆ่าเชื้อ (CSSD Instrument Information)
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.8em', color: '#64748b' }}>
                    ระบุรหัสบาร์โค้ดเครื่องมือ หรือติ๊กเลือกหากเป็นเหตุการณ์ทั่วไปที่ไม่มีเครื่องมือเฉพาะเจาะจง
                  </p>
                </div>
              </div>

              {/* Checkbox: ไม่มีข้อมูลของเครื่องมือ */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '12px 14px',
                  backgroundColor: formData.no_instrument_data ? '#f0fdfa' : '#ffffff',
                  border: `1.5px solid ${formData.no_instrument_data ? '#0d9488' : '#cbd5e1'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  marginBottom: '16px',
                  transition: 'all 0.15s ease'
                }}
              >
                <input
                  type="checkbox"
                  id="checkbox-no-instrument"
                  data-testid="checkbox-no-instrument"
                  checked={formData.no_instrument_data}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      no_instrument_data: e.target.checked,
                      instrument_barcode: e.target.checked ? '' : prev.instrument_barcode
                    }))
                  }
                  style={{ marginTop: '3px', cursor: 'pointer', width: '17px', height: '17px' }}
                />
                <div>
                  <span style={{ fontSize: '0.9em', fontWeight: '700', color: '#1e293b' }}>
                    ไม่มีข้อมูลของเครื่องมือ / ไม่ทราบเลขบาร์โค้ด
                  </span>
                  <span style={{ display: 'block', fontSize: '0.78em', color: '#64748b', marginTop: '2px' }}>
                    (เช่น เหตุการณ์ทั่วไปในแผนกจ่ายกลาง หรือไม่มีเครื่องมือเฉพาะเจาะจง)
                  </span>
                </div>
              </label>

              {/* Instrument Barcode Field */}
              <div>
                <label
                  htmlFor="input-instrument-barcode"
                  style={{
                    display: 'block',
                    fontSize: '0.85em',
                    fontWeight: '700',
                    color: formData.no_instrument_data ? '#94a3b8' : '#334155',
                    marginBottom: '6px'
                  }}
                >
                  รหัสบาร์โค้ดเครื่องมือ หรือเลขที่ล็อต (Lot / Barcode No.)
                </label>
                <input
                  id="input-instrument-barcode"
                  type="text"
                  data-testid="input-instrument-barcode"
                  disabled={formData.no_instrument_data}
                  value={formData.instrument_barcode}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, instrument_barcode: e.target.value }))
                  }
                  placeholder={
                    formData.no_instrument_data
                      ? '(ไม่มีข้อมูลของเครื่องมือ)'
                      : 'เช่น SET-EXT-001 หรือ 20261005-01'
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '14px',
                    border: `1.5px solid ${formData.no_instrument_data ? '#e2e8f0' : '#cbd5e1'}`,
                    backgroundColor: formData.no_instrument_data ? '#f1f5f9' : '#ffffff',
                    color: formData.no_instrument_data ? '#94a3b8' : '#0f172a',
                    fontSize: '0.95em',
                    fontWeight: '700',
                    outline: 'none',
                    cursor: formData.no_instrument_data ? 'not-allowed' : 'text',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          )}

          {/* Photo Upload Zone (Included for all 3 formats: Clinical, CSSD, Non-Clinical) */}
          <div
            data-testid="section-photo-upload"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '20px 22px',
              marginBottom: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '20px' }}>📷</span>
              <div>
                <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  แนบรูปภาพเพิ่มเติม (ถ้ามี)
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.8em', color: '#64748b' }}>
                  {formData.risk_domain === 'clinical' &&
                    '(ถ้ามี) ภาพถ่ายทางคลินิก แผล ชิ้นฟัน หรือฟิล์ม X-ray ประกอบเหตุการณ์'}
                  {formData.risk_domain === 'cssd' &&
                    '(ถ้ามี) ภาพห่อ/ซองเครื่องมือ รอยฉีกขาด ความชื้น แถบเคมีอินดิเคเตอร์ หรือเครื่องมือที่ชำรุด'}
                  {formData.risk_domain === 'non_clinical' &&
                    '(ถ้ามี) ภาพบริเวณที่เกิดเหตุ สิ่งแวดล้อม อุปกรณ์ชำรุด รอยรั่วซึม หรือจุดเสี่ยงทางกายภาพ'}
                </p>
              </div>
            </div>

            {/* Hidden File Input & Trigger Area */}
            <input
              type="file"
              id="wizard-photo-upload-input"
              data-testid="input-file-upload"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="wizard-photo-upload-input"
              data-testid="btn-upload-photo"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '22px 16px',
                border: '2px dashed #cbd5e1',
                borderRadius: '14px',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                marginTop: '12px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#0284c7';
                e.currentTarget.style.backgroundColor = '#f0f9ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              <span style={{ fontSize: '28px', marginBottom: '6px' }}>📁</span>
              <span style={{ fontSize: '0.92em', fontWeight: '700', color: '#0284c7' }}>
                แตะเพื่อเลือกรูปภาพ หรือลากไฟล์มาวางที่นี่
              </span>
              <span style={{ fontSize: '0.78em', color: '#94a3b8', marginTop: '4px' }}>
                รองรับไฟล์ภาพ JPG, PNG, WEBP (เลือกได้หลายภาพ)
              </span>
            </label>

            {/* Thumbnail Gallery */}
            {formData.images && formData.images.length > 0 && (
              <div
                data-testid="gallery-uploaded-images"
                style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  marginTop: '14px'
                }}
              >
                {formData.images.map((img, idx) => (
                  <div
                    key={img.id || idx}
                    data-testid={`thumbnail-image-${idx}`}
                    style={{
                      position: 'relative',
                      width: '88px',
                      height: '88px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1.5px solid #e2e8f0',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                    }}
                  >
                    <img
                      src={img.url || img.dataUrl}
                      alt={img.name || `image-${idx}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      data-testid={`btn-remove-image-${idx}`}
                      onClick={() => handleRemoveImage(idx)}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: 'rgba(15, 23, 42, 0.75)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        transition: 'background-color 0.15s ease'
                      }}
                      title="ลบรูปภาพ"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ================= COMMON OPTIONAL FIELDS ================= */}

          {/* Additional Description Card */}
          <div
            data-testid="section-additional-description"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '20px 22px',
              marginBottom: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '20px' }}>📝</span>
              <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                รายละเอียดเพิ่มเติม / การแก้ไขเบื้องต้น (ถ้ามี)
              </h3>
            </div>
            <textarea
              data-testid="textarea-description"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="เช่น อธิบายเหตุการณ์เพิ่มเติม หรือการให้การดูแลรักษา/แก้ไขปัญหาเฉพาะหน้าเบื้องต้น..."
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '14px',
                border: '1.5px solid #cbd5e1',
                backgroundColor: '#ffffff',
                fontSize: '0.92em',
                fontWeight: '500',
                color: '#0f172a',
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Identity & Anonymous Settings Card */}
          <div
            data-testid="section-identity-settings"
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '18px',
              border: '1.5px solid #e2e8f0',
              padding: '20px 22px',
              marginBottom: '26px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '20px' }}>👤</span>
              <h3 style={{ fontSize: '1.05em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                ข้อมูลผู้รายงาน (Reporter Information)
              </h3>
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                marginBottom: formData.is_anonymous ? 0 : '14px'
              }}
            >
              <input
                type="checkbox"
                id="checkbox-anonymous"
                data-testid="checkbox-anonymous"
                checked={formData.is_anonymous}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_anonymous: e.target.checked }))}
                style={{ cursor: 'pointer', width: '17px', height: '17px' }}
              />
              <span style={{ fontSize: '0.92em', fontWeight: '700', color: '#1e293b' }}>
                🔒 รายงานแบบนิรนาม (ไม่เปิดเผยตัวตน)
              </span>
            </label>

            {!formData.is_anonymous && (
              <div>
                <label
                  htmlFor="input-reporter-name"
                  style={{
                    display: 'block',
                    fontSize: '0.85em',
                    fontWeight: '700',
                    color: '#334155',
                    marginBottom: '6px'
                  }}
                >
                  ชื่อ-สกุล หรือตำแหน่งผู้รายงาน
                </label>
                <input
                  id="input-reporter-name"
                  type="text"
                  data-testid="input-reporter-name"
                  value={formData.reporter_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, reporter_name: e.target.value }))
                  }
                  placeholder="ชื่อ-สกุล หรือตำแหน่งผู้รายงาน (ไม่ระบุก็ได้)"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '14px',
                    border: '1.5px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    fontSize: '0.92em',
                    fontWeight: '600',
                    color: '#0f172a',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
          </div>

          {/* Bottom Navigation Actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '14px',
              paddingTop: '20px',
              borderTop: '1px solid #f1f5f9'
            }}
          >
            <button
              type="button"
              data-testid="btn-back-to-step4"
              onClick={handleBackToStep4}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 22px',
                borderRadius: '14px',
                backgroundColor: '#f1f5f9',
                border: '1.5px solid #cbd5e1',
                color: '#334155',
                fontSize: '0.92em',
                fontWeight: '800',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e2e8f0';
                e.currentTarget.style.borderColor = '#94a3b8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
            >
              <span>← ย้อนกลับไปตรวจสอบข้อมูล</span>
            </button>

            <button
              type="button"
              data-testid="btn-submit-wizard"
              onClick={handleSubmitReport}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 34px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
                color: '#ffffff',
                border: 'none',
                fontSize: '1.05em',
                fontWeight: '800',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(5, 150, 105, 0.35)',
                transition: 'all 0.18s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(5, 150, 105, 0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(5, 150, 105, 0.35)';
              }}
            >
              <span>⚡ ส่งรายงานความเสี่ยง (Submit Incident Report)</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedRiskReportWizard;

